import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Badge, type BadgeProps } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { toast } from '@/design-system/toaster';
import { Invoice, invoicesAPI } from '@/services/api/invoices';
import { trackCodeFromScan } from '@/utils';
import { AgingBadge } from './InvoiceBadges';

type BadgeStatus = NonNullable<BadgeProps['status']>;

export type ScanMode = 'receive' | 'submit';

type EntryStatus =
  | 'ready'
  | 'already_received'
  | 'already_submitted'
  | 'not_yet_received'
  | 'with_rider'
  | 'not_found'
  | 'received'
  | 'submitted'
  | 'error';

interface ScanEntry {
  key: number;
  rawInput: string;
  invoice: Invoice | null;
  status: EntryStatus;
  errorMessage?: string;
}

interface ScanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReceived: (invoices: Invoice[]) => void;
  onSubmitted?: (invoices: Invoice[]) => void;
  presetInvoice?: Invoice | null;
  initialMode?: ScanMode;
}

function classifyForMode(invoice: Invoice, mode: ScanMode): EntryStatus {
  if (mode === 'receive') {
    // RECEIVED_BY_RIDER moves to RECEIVED_AT_HUB on hub scan.
    return invoice.status === 'PENDING_COLLECTION' ||
      invoice.status === 'RECEIVED_BY_RIDER' ||
      invoice.status === 'EXCEPTION'
      ? 'ready'
      : 'already_received';
  }
  // submit mode — both hub-received and rider-held invoices can be handed to
  // the client for confirmation.
  if (
    invoice.status === 'RECEIVED_AT_HUB' ||
    invoice.status === 'RECEIVED_BY_RIDER'
  )
    return 'ready';
  if (invoice.status === 'PENDING_COLLECTION' || invoice.status === 'EXCEPTION')
    return 'not_yet_received';
  return 'already_submitted';
}

const ScanModal: React.FC<ScanModalProps> = ({
  isOpen,
  onClose,
  onReceived,
  onSubmitted,
  presetInvoice = null,
  initialMode = 'receive',
}) => {
  const [mode, setMode] = useState<ScanMode>(initialMode);
  const [scanValue, setScanValue] = useState('');
  const [queue, setQueue] = useState<ScanEntry[]>([]);
  const [isLooking, setIsLooking] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [flashKey, setFlashKey] = useState<number | null>(null);
  const [confirmIntent, setConfirmIntent] = useState<'close' | 'clear' | null>(
    null
  );
  const keyRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listEndRef = useRef<HTMLDivElement>(null);

  const nextKey = () => {
    keyRef.current += 1;
    return keyRef.current;
  };

  const focusInput = () => {
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setScanValue('');
    setIsLooking(false);
    setIsCommitting(false);
    setFlashKey(null);
    setConfirmIntent(null);

    if (presetInvoice) {
      setQueue([
        {
          key: nextKey(),
          rawInput: presetInvoice.invoiceNumber,
          invoice: presetInvoice,
          status: classifyForMode(presetInvoice, initialMode),
        },
      ]);
    } else {
      setQueue([]);
    }
    focusInput();
  }, [isOpen, presetInvoice, initialMode]);

  const switchMode = (next: ScanMode) => {
    if (isCommitting || next === mode) return;
    setMode(next);
    setQueue((prev) =>
      prev.map((e) =>
        e.invoice && e.status !== 'received' && e.status !== 'submitted'
          ? { ...e, status: classifyForMode(e.invoice, next) }
          : e
      )
    );
    focusInput();
  };

  useEffect(() => {
    if (queue.length > 0) {
      listEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [queue.length]);

  const flashEntry = (key: number) => {
    setFlashKey(key);
    setTimeout(() => setFlashKey((cur) => (cur === key ? null : cur)), 900);
  };

  const handleScan = async () => {
    // A per-item label QR scans as a tracking URL (…/track/{code}); reduce it to
    // the bare code so the client-side match + lookup work on invoice/AWB.
    const value = trackCodeFromScan(scanValue);
    if (!value || isLooking || isCommitting) return;

    const duplicate = queue.find(
      (e) =>
        e.invoice &&
        (e.invoice.invoiceNumber.toLowerCase() === value.toLowerCase() ||
          e.invoice.airwaybill.toLowerCase() === value.toLowerCase())
    );
    if (duplicate) {
      flashEntry(duplicate.key);
      setScanValue('');
      focusInput();
      return;
    }

    setIsLooking(true);
    setScanValue('');
    try {
      const result = await invoicesAPI.lookup(value);
      // A booking_id / Delivery Note scan resolves to the WHOLE group; a plain
      // invoice-number scan resolves to a single invoice. Append every invoice
      // the lookup returned, deduped against what's already queued. Fall back to
      // the primary `invoice` when an older backend omits `invoices`.
      const found =
        result.invoices && result.invoices.length > 0
          ? result.invoices
          : [result.invoice];
      setQueue((prev) => {
        const existingIds = new Set(
          prev.map((e) => e.invoice?.id).filter(Boolean)
        );
        const fresh = found.filter((inv) => !existingIds.has(inv.id));
        if (fresh.length === 0) {
          // Whole group already in the queue — flash the first match instead.
          const dup = prev.find((e) => e.invoice?.id === found[0]?.id);
          if (dup) flashEntry(dup.key);
          return prev;
        }
        const newEntries: ScanEntry[] = fresh.map((inv) => ({
          key: nextKey(),
          rawInput: value,
          invoice: inv,
          status: classifyForMode(inv, mode),
        }));
        flashEntry(newEntries[0].key);
        return [...prev, ...newEntries];
      });
    } catch (err) {
      const key = nextKey();
      flashEntry(key);
      setQueue((prev) => [
        ...prev,
        {
          key,
          rawInput: value,
          invoice: null,
          status: 'not_found',
          errorMessage:
            err instanceof Error ? err.message : 'Invoice not found.',
        },
      ]);
    } finally {
      setIsLooking(false);
      focusInput();
    }
  };

  const removeEntry = (key: number) => {
    setQueue((prev) => prev.filter((e) => e.key !== key));
    focusInput();
  };

  const clearQueue = () => {
    setQueue([]);
    setScanValue('');
    focusInput();
  };

  // Guarded close — used by Cancel, the X, and backdrop click. When there are
  // queued scans we ask before discarding; otherwise close straight away.
  const requestClose = () => {
    if (isCommitting) return;
    if (queue.length > 0) {
      setConfirmIntent('close');
      return;
    }
    onClose();
  };

  // Clear queue is only reachable with a non-empty queue, so always confirm.
  const requestClear = () => {
    if (isCommitting) return;
    setConfirmIntent('clear');
  };

  const handleConfirmDiscard = () => {
    const intent = confirmIntent;
    setConfirmIntent(null);
    if (intent === 'close') {
      onClose();
    } else if (intent === 'clear') {
      clearQueue();
    }
  };

  const counts = useMemo(() => {
    const scanned = queue.length;
    const ready = queue.filter((e) => e.status === 'ready').length;
    const alreadyReceived = queue.filter(
      (e) => e.status === 'already_received'
    ).length;
    const alreadySubmitted = queue.filter(
      (e) => e.status === 'already_submitted'
    ).length;
    const notYetReceived = queue.filter(
      (e) => e.status === 'not_yet_received'
    ).length;
    const withRider = queue.filter((e) => e.status === 'with_rider').length;
    const received = queue.filter((e) => e.status === 'received').length;
    const submitted = queue.filter((e) => e.status === 'submitted').length;
    const errors = queue.filter(
      (e) => e.status === 'not_found' || e.status === 'error'
    ).length;
    return {
      scanned,
      ready,
      alreadyReceived,
      alreadySubmitted,
      notYetReceived,
      withRider,
      received,
      submitted,
      errors,
    };
  }, [queue]);

  const handleCommit = async () => {
    const targets = queue.filter((e) => e.status === 'ready' && e.invoice);
    if (targets.length === 0) return;
    setIsCommitting(true);

    const updatedInvoices: Invoice[] = [];
    const errorsByKey = new Map<number, string>();

    if (mode === 'submit') {
      try {
        const result = await invoicesAPI.bulkSubmitForConfirm(
          targets.map((t) => t.invoice!.id)
        );
        updatedInvoices.push(...result.updated);
        const errorById = new Map(
          result.errors.map((err) => [err.id, err.message])
        );
        for (const t of targets) {
          const msg = errorById.get(t.invoice!.id);
          if (msg) errorsByKey.set(t.key, msg);
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to submit batch.';
        for (const t of targets) errorsByKey.set(t.key, msg);
      }
    } else {
      const results = await Promise.allSettled(
        targets.map((t) => invoicesAPI.receive(t.invoice!.id))
      );
      results.forEach((res, idx) => {
        const target = targets[idx];
        if (res.status === 'fulfilled') {
          updatedInvoices.push(res.value);
        } else {
          errorsByKey.set(
            target.key,
            res.reason instanceof Error
              ? res.reason.message
              : 'Failed to confirm.'
          );
        }
      });
    }

    const updatedById = new Map(updatedInvoices.map((inv) => [inv.id, inv]));
    const successStatus: EntryStatus =
      mode === 'receive' ? 'received' : 'submitted';

    setQueue((prev) =>
      prev.map((e) => {
        if (!e.invoice) return e;
        const updated = updatedById.get(e.invoice.id);
        if (updated) {
          return { ...e, invoice: updated, status: successStatus };
        }
        const errorMessage = errorsByKey.get(e.key);
        if (errorMessage) {
          return { ...e, status: 'error', errorMessage };
        }
        return e;
      })
    );

    setIsCommitting(false);

    if (updatedInvoices.length > 0) {
      onReceived(updatedInvoices);
      const verbedTitle =
        mode === 'receive'
          ? `${updatedInvoices.length} invoice${updatedInvoices.length === 1 ? '' : 's'} received`
          : `${updatedInvoices.length} invoice${updatedInvoices.length === 1 ? '' : 's'} submitted`;
      const verbedBody =
        mode === 'receive'
          ? 'Marked as received at hub.'
          : 'Submitted to client for confirmation.';
      toast.success(verbedTitle, { description: verbedBody });

      if (mode === 'submit' && onSubmitted) {
        onSubmitted(updatedInvoices);
      }
    }
    if (errorsByKey.size > 0) {
      toast.error(`${errorsByKey.size} failed to confirm`, {
        description: 'Check errors in the list and retry.',
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  const modeSwitcher = (
    <SegmentedControl
      value={mode}
      onValueChange={(v: string) => {
        if (v) switchMode(v as ScanMode);
      }}
      size="sm"
      aria-label="Scan mode"
    >
      <SegmentedItem value="receive" disabled={isCommitting}>
        Receive
      </SegmentedItem>
      <SegmentedItem value="submit" disabled={isCommitting}>
        Submit to client
      </SegmentedItem>
    </SegmentedControl>
  );

  return (
    <>
      <FormModal
        open={isOpen}
        onClose={requestClose}
        icon={<i className="ri-qr-scan-2-line" />}
        iconTone="primary"
        title="Scan surat jalan"
        subtitle={
          mode === 'receive'
            ? 'Receive mode — marks scanned invoices as received at hub.'
            : 'Submit mode — submits received invoices to the client for confirmation.'
        }
        headerExtra={modeSwitcher}
        size="xl"
        padded={false}
        closeOnBackdropClick={!isCommitting}
        closeOnEscape={false}
        footer={
          <div className="flex items-center gap-2">
            <Button
              tone="neutral" style="stroke"
              onClick={requestClear}
              disabled={queue.length === 0 || isCommitting}
            >
              Clear queue
            </Button>
            <div className="flex-1" />
            <Button
              tone="neutral" style="stroke"
              onClick={requestClose}
              disabled={isCommitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCommit}
              disabled={counts.ready === 0 || isCommitting}
            >
              {isCommitting
                ? 'Confirming…'
                : mode === 'receive'
                  ? counts.ready > 0
                    ? `Confirm ${counts.ready} received`
                    : 'Confirm received'
                  : counts.ready > 0
                    ? `Confirm ${counts.ready} submitted`
                    : 'Confirm submitted'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 border-b border-stroke-soft-200 px-5 py-4">
          <div className="relative">
            <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-text-soft-400" />
            <input
              ref={inputRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleScan();
                }
              }}
              placeholder="Scan or type invoice number, then press Enter"
              disabled={isCommitting}
              autoFocus
              className="w-full rounded-lg border border-stroke-soft-200 bg-bg-white-0 py-2.5 pl-9 pr-10 font-mono text-sm tracking-tight text-text-strong-950 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent disabled:bg-bg-weak-50"
            />
            {isLooking && (
              <i className="ri-loader-4-line absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[16px] text-accent" />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <Badge status="neutral">
              <span className="font-bold tabular-nums">{counts.scanned}</span>
              <span className="ml-1 text-[11px] opacity-80">Scanned</span>
            </Badge>
            <Badge status="success">
              <span className="font-bold tabular-nums">{counts.ready}</span>
              <span className="ml-1 text-[11px] opacity-80">Ready</span>
            </Badge>
            {counts.received > 0 && (
              <Badge status="feature">
                <span className="font-bold tabular-nums">
                  {counts.received}
                </span>
                <span className="ml-1 text-[11px] opacity-80">Received</span>
              </Badge>
            )}
            {counts.submitted > 0 && (
              <Badge status="feature">
                <span className="font-bold tabular-nums">
                  {counts.submitted}
                </span>
                <span className="ml-1 text-[11px] opacity-80">Submitted</span>
              </Badge>
            )}
            {counts.alreadyReceived > 0 && (
              <Badge status="away">
                <span className="font-bold tabular-nums">
                  {counts.alreadyReceived}
                </span>
                <span className="ml-1 text-[11px] opacity-80">
                  Already received
                </span>
              </Badge>
            )}
            {counts.alreadySubmitted > 0 && (
              <Badge status="away">
                <span className="font-bold tabular-nums">
                  {counts.alreadySubmitted}
                </span>
                <span className="ml-1 text-[11px] opacity-80">
                  Already submitted
                </span>
              </Badge>
            )}
            {counts.notYetReceived > 0 && (
              <Badge status="away">
                <span className="font-bold tabular-nums">
                  {counts.notYetReceived}
                </span>
                <span className="ml-1 text-[11px] opacity-80">
                  Not yet received
                </span>
              </Badge>
            )}
            {counts.withRider > 0 && (
              <Badge status="away">
                <span className="font-bold tabular-nums">
                  {counts.withRider}
                </span>
                <span className="ml-1 text-[11px] opacity-80">With rider</span>
              </Badge>
            )}
            {counts.errors > 0 && (
              <Badge status="error">
                <span className="font-bold tabular-nums">{counts.errors}</span>
                <span className="ml-1 text-[11px] opacity-80">Errors</span>
              </Badge>
            )}
          </div>
        </div>

        <div className="px-5 py-4">
          {queue.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-stroke-soft-200 bg-bg-weak-50 py-12 text-center">
              <i className="ri-barcode-box-line text-[32px] text-text-soft-400" />
              <p className="mt-2 text-sm font-semibold tracking-tight text-text-sub-600">
                Ready to scan
              </p>
              <p className="text-xs text-text-soft-400 mt-1">
                Each scan adds to the queue. Confirm them all at once.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {queue.map((entry, idx) => (
                <EntryRow
                  key={entry.key}
                  entry={entry}
                  index={idx + 1}
                  flashing={flashKey === entry.key}
                  disabled={isCommitting}
                  onRemove={() => removeEntry(entry.key)}
                />
              ))}
              <div ref={listEndRef} />
            </ul>
          )}
        </div>
      </FormModal>

      <FormModal
        open={confirmIntent !== null}
        onClose={() => setConfirmIntent(null)}
        icon={<i className="ri-error-warning-line" />}
        iconTone="danger"
        title={`Discard ${queue.length} scanned invoice${
          queue.length === 1 ? '' : 's'
        }?`}
        subtitle={
          confirmIntent === 'clear'
            ? 'The queue will be emptied. This cannot be undone.'
            : 'You have scans that haven’t been confirmed yet. Closing will discard them.'
        }
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button tone="neutral" style="stroke" onClick={() => setConfirmIntent(null)}>
              Keep scanning
            </Button>
            <Button tone="destructive" onClick={handleConfirmDiscard}>
              {confirmIntent === 'clear' ? 'Clear queue' : 'Discard & close'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-sub-600">
          {queue.length} scanned invoice{queue.length === 1 ? '' : 's'} will be
          discarded.
        </p>
      </FormModal>
    </>
  );
};

const STATUS_META: Record<
  EntryStatus,
  { label: string; status: BadgeStatus; icon: string }
> = {
  ready: { label: 'Ready', status: 'success', icon: 'ri-checkbox-circle-line' },
  already_received: {
    label: 'Already received',
    status: 'away',
    icon: 'ri-information-line',
  },
  already_submitted: {
    label: 'Already submitted',
    status: 'away',
    icon: 'ri-information-line',
  },
  not_yet_received: {
    label: 'Not yet received',
    status: 'away',
    icon: 'ri-error-warning-line',
  },
  with_rider: {
    label: 'With rider',
    status: 'away',
    icon: 'ri-error-warning-line',
  },
  received: {
    label: 'Received',
    status: 'feature',
    icon: 'ri-checkbox-circle-fill',
  },
  submitted: {
    label: 'Submitted',
    status: 'feature',
    icon: 'ri-checkbox-circle-fill',
  },
  not_found: {
    label: 'Not found',
    status: 'error',
    icon: 'ri-error-warning-line',
  },
  error: { label: 'Failed', status: 'error', icon: 'ri-close-circle-line' },
};

const EntryRow: React.FC<{
  entry: ScanEntry;
  index: number;
  flashing: boolean;
  disabled: boolean;
  onRemove: () => void;
}> = ({ entry, index, flashing, disabled, onRemove }) => {
  const meta = STATUS_META[entry.status];
  return (
    <li
      className={`flex items-center gap-3 rounded-lg border bg-bg-white-0 p-3 transition-all ${
        flashing
          ? 'border-accent shadow-[0_0_0_3px_rgba(94,42,172,0.15)]'
          : 'border-stroke-soft-200'
      }`}
    >
      <span className="w-5 shrink-0 text-center text-xs font-bold tabular-nums text-text-soft-400">
        {index}
      </span>
      <Badge status={meta.status} size="md" className="shrink-0">
        <i className={`${meta.icon} text-[12px]`} />
        {meta.label}
      </Badge>
      <div className="min-w-0 flex-1">
        {entry.invoice ? (
          <>
            <p className="truncate font-mono text-sm font-semibold text-text-strong-950">
              {entry.invoice.invoiceNumber}
            </p>
            <p className="truncate text-xs text-text-sub-600">
              AWB {entry.invoice.airwaybill} · {entry.invoice.client.name} ·{' '}
              {entry.invoice.outlet?.name ?? '—'}
              {entry.invoice.outlet &&
                !entry.invoice.outlet.id &&
                entry.invoice.outlet.name && (
                  <Badge status="neutral" size="sm" className="ml-1">
                    Drop-in
                  </Badge>
                )}
            </p>
            {entry.invoice.outlet?.address && (
              <p
                className="truncate text-[11px] text-text-sub-600"
                title={entry.invoice.outlet.address}
              >
                <i className="ri-map-pin-line mr-1 text-[12px] text-text-soft-400" />
                {entry.invoice.outlet.address}
              </p>
            )}
            {entry.status === 'not_yet_received' && (
              <p className="truncate text-[11px] text-(--state-warning-dark)">
                Not yet received — switch to Receive mode or scan after
                receiving.
              </p>
            )}
            {entry.status === 'with_rider' && (
              <p className="truncate text-[11px] text-(--state-warning-dark)">
                Received by rider — must be scanned at hub first. Switch to
                Receive mode.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="truncate font-mono text-sm font-semibold text-text-strong-950">
              {entry.rawInput}
            </p>
            <p className="truncate text-xs text-error-base">
              {entry.errorMessage ?? 'Invoice not found.'}
            </p>
          </>
        )}
      </div>
      {entry.invoice && entry.invoice.agingTier && (
        <div className="shrink-0">
          <AgingBadge
            tier={entry.invoice.agingTier}
            daysSince={entry.invoice.daysSince}
          />
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="shrink-0 rounded-md p-1 text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600 disabled:opacity-40"
        aria-label="Remove from queue"
      >
        <i className="ri-close-line text-[18px]" />
      </button>
    </li>
  );
};

export default ScanModal;
